import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  AlertIcon,
  Badge,
  Box,
  Button,
  Checkbox,
  Flex,
  FormControl,
  FormErrorMessage,
  FormLabel,
  HStack,
  Icon,
  Image,
  Input,
  Modal,
  ModalBody,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Progress,
  ScaleFade,
  SimpleGrid,
  Tag,
  TagCloseButton,
  TagLabel,
  Text,
  Textarea,
  Tooltip,
  VStack,
  useColorModeValue,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import { Autocomplete } from '@react-google-maps/api';
import {
  MdArticle,
  MdCheck,
  MdCheckCircle,
  MdClass,
  MdCloudUpload,
  MdCreditCard,
  MdFolder,
  MdKeyboardArrowDown,
  MdLocationOn,
  MdPerson,
  MdRocketLaunch,
} from 'react-icons/md';
import { addDoc, arrayUnion, collection, doc, getFirestore, updateDoc } from 'firebase/firestore';
import {
  getDownloadURL,
  getStorage,
  ref,
  uploadBytesResumable,
} from 'firebase/storage';
import GoogleMapsLoader from 'components/map/GoogleMapsLoader';
import Card from 'components/card/Card';
import { AuthContext } from 'contexts/AuthContext';
import { app } from 'lib/firebase';
import { CLASS_TYPE_ICONS, getClassTypeIcon } from 'utils/classIcons';
import {
  INSTRUCTOR_DOCUMENT_SECTIONS,
  buildInstructorDocumentEntry,
  waitForInstructorDocumentUpload,
} from 'utils/instructorDocuments';
import { TRAVEL_DISTANCE_OPTIONS, normalizeInstructorClassTypes } from 'utils/instructorProfile';
import {
  buildInstructorOnboardingCompletedActivity,
  buildInstructorOnboardingStripeSkippedActivity,
  buildInstructorOnboardingUpdate,
  getMissingInstructorOnboardingFields,
  isInstructorOnboardingComplete,
  isValidOnboardingPhoneNumber,
  normalizeUserEnteredClassTypes,
} from 'utils/instructorOnboarding';

const db = getFirestore(app);
const storage = getStorage(app);
const INSTRUCTOR_AGREEMENT_URL = 'https://firebasestorage.googleapis.com/v0/b/fitgroupusaapp-prod.appspot.com/o/Business%20Service%20Provider%20Agreement_INSTRUCTOR%20AGREEMENT.pdf?alt=media&token=3e56b59e-859b-49a6-9d82-719431dbb33e';
const INSTRUCTOR_AGREEMENT_VIEWER_URL = `${INSTRUCTOR_AGREEMENT_URL}#toolbar=0&navpanes=0&view=FitH`;
const AGREEMENT_CHECKBOX_LABEL = 'By checking this checkbox, I confirm that I have read and agree with the agreement.';
const US_ADDRESS_AUTOCOMPLETE_OPTIONS = {
  componentRestrictions: { country: 'us' },
  fields: ['address_components', 'formatted_address', 'geometry'],
  types: ['address'],
};
const STATE_COMPONENT_TYPES = ['administrative_area_level_1'];
const CITY_COMPONENT_TYPES = ['locality', 'postal_town', 'sublocality'];

const getAddressComponent = (place, types, field = 'long_name') => {
  const component = place?.address_components?.find((item) => (
    types.some((type) => item.types.includes(type))
  ));

  return component?.[field] || '';
};

const STEPS = [
  { label: 'Welcome / Identity', icon: MdPerson },
  { label: 'Home Base', icon: MdLocationOn },
  { label: 'Travel Distance', icon: MdRocketLaunch },
  { label: 'Class Types', icon: MdClass },
  { label: 'Stripe Payouts', icon: MdCreditCard },
  { label: 'Documents', icon: MdFolder },
  { label: 'Agreement', icon: MdArticle },
];

const createInitialForm = (userData = {}) => {
  const data = userData || {};

  return {
    FirstName: data.FirstName || '',
    LastName: data.LastName || '',
    Phone: data.Phone || '',
    Address: data.Address || data.StreetAddress || '',
    City: data.City || '',
    State: data.State || '',
    Zip: data.Zip || data.ZipCode || '',
    location: data.location || null,
    distanceValue: data.distanceValue || '',
    ClassTypes: normalizeInstructorClassTypes(data.ClassTypes),
    CustomClassTypes: normalizeUserEnteredClassTypes(data.CustomClassTypes).map((item) => item.value),
  };
};

export default function InstructorOnboarding() {
  const {
    user,
    userData,
    userDocId,
    refreshUserData,
    stripeSetupComplete,
    stripeSetupLinkLoading,
    openStripeSetup,
  } = useContext(AuthContext);
  const [currentStep, setCurrentStep] = useState(0);
  const [form, setForm] = useState(() => createInitialForm(userData));
  const [selectedClassTypes, setSelectedClassTypes] = useState(() => normalizeInstructorClassTypes(userData?.ClassTypes));
  const [customClassTypes, setCustomClassTypes] = useState(() => (
    normalizeUserEnteredClassTypes(userData?.CustomClassTypes).map((item) => item.value)
  ));
  const [customClassTypeInput, setCustomClassTypeInput] = useState('');
  const [documentCategory, setDocumentCategory] = useState('Insurance');
  const [documentFile, setDocumentFile] = useState(null);
  const [documentNotes, setDocumentNotes] = useState('');
  const [isDocumentTypeOpen, setIsDocumentTypeOpen] = useState(false);
  const [documentUploadProgress, setDocumentUploadProgress] = useState(0);
  const [isUploadingDocument, setIsUploadingDocument] = useState(false);
  const [hasScrolledAgreement, setHasScrolledAgreement] = useState(false);
  const [agreementAccepted, setAgreementAccepted] = useState(false);
  const [stripeSkipped, setStripeSkipped] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const skipStripeDisclosure = useDisclosure();
  const cancelSkipRef = useRef(null);
  const autocompleteRef = useRef(null);
  const documentInputRef = useRef(null);
  const initializedUserDocRef = useRef(null);
  const successTimerRef = useRef(null);
  const toast = useToast();

  const textColor = useColorModeValue('secondaryGray.900', 'white');
  const mutedText = useColorModeValue('gray.600', 'gray.400');
  const modalBg = useColorModeValue('white', 'gray.900');
  const softBg = useColorModeValue('blue.50', 'whiteAlpha.100');
  const panelBg = useColorModeValue('gray.50', 'whiteAlpha.100');
  const borderColor = useColorModeValue('gray.200', 'whiteAlpha.200');
  const brandColor = useColorModeValue('brand.500', 'brand.400');
  const selectedBg = useColorModeValue('blue.50', 'whiteAlpha.200');
  const inputBg = useColorModeValue('white', 'whiteAlpha.100');

  useEffect(() => {
    if (!userDocId) {
      initializedUserDocRef.current = null;
      return;
    }
    if (!userData || initializedUserDocRef.current === userDocId) return;

    initializedUserDocRef.current = userDocId;
    setForm(createInitialForm(userData));
    setSelectedClassTypes(normalizeInstructorClassTypes(userData?.ClassTypes));
    setCustomClassTypes(normalizeUserEnteredClassTypes(userData?.CustomClassTypes).map((item) => item.value));
    setCustomClassTypeInput('');
  }, [userData, userDocId]);

  useEffect(() => () => {
    if (successTimerRef.current) {
      window.clearTimeout(successTimerRef.current);
    }
  }, []);

  const hydratedForm = useMemo(() => ({
    ...form,
    StreetAddress: form.Address,
    ClassTypes: selectedClassTypes,
    CustomClassTypes: customClassTypes,
    agreementAccepted,
    agreementUrl: INSTRUCTOR_AGREEMENT_URL,
  }), [form, selectedClassTypes, customClassTypes, agreementAccepted]);

  const shouldShow = Boolean(
    userDocId
    && userData
    && !isInstructorOnboardingComplete(userData)
    && !showSuccess
  );

  const progressPercent = ((currentStep + 1) / STEPS.length) * 100;
  const availableClassTypes = useMemo(() => Object.keys(CLASS_TYPE_ICONS), []);

  const updateField = (field, value) => {
    setForm((current) => ({ ...current, [field]: value }));
  };

  const handlePlaceChanged = () => {
    const place = autocompleteRef.current?.getPlace();
    if (!place) return;

    const latitude = place.geometry?.location?.lat?.();
    const longitude = place.geometry?.location?.lng?.();

    setForm((current) => ({
      ...current,
      Address: place.formatted_address || current.Address,
      City: getAddressComponent(place, CITY_COMPONENT_TYPES) || current.City,
      State: getAddressComponent(place, STATE_COMPONENT_TYPES, 'short_name') || current.State,
      Zip: getAddressComponent(place, ['postal_code']) || current.Zip,
      location: Number.isFinite(latitude) && Number.isFinite(longitude)
        ? { latitude, longitude }
        : current.location,
    }));
  };

  const toggleClassType = (classType) => {
    setSelectedClassTypes((current) => (
      current.includes(classType)
        ? current.filter((item) => item !== classType)
        : [...current, classType]
    ));
  };

  const addCustomClassType = () => {
    const value = customClassTypeInput.trim().replace(/\s+/g, ' ');
    if (!value) return;

    setCustomClassTypes((current) => {
      const alreadyExists = current.some((item) => item.toLowerCase() === value.toLowerCase());
      return alreadyExists ? current : [...current, value];
    });
    setCustomClassTypeInput('');
  };

  const removeCustomClassType = (classType) => {
    setCustomClassTypes((current) => (
      current.filter((item) => item.toLowerCase() !== classType.toLowerCase())
    ));
  };

  const handleCustomClassTypeKeyDown = (event) => {
    if (event.key !== 'Enter') return;
    event.preventDefault();
    addCustomClassType();
  };

  const getStepMissingFields = () => {
    if (currentStep === 0) {
      return getMissingInstructorOnboardingFields(hydratedForm)
        .filter((field) => ['FirstName', 'LastName', 'Phone'].includes(field));
    }
    if (currentStep === 1) {
      return getMissingInstructorOnboardingFields(hydratedForm)
        .filter((field) => ['Address', 'StreetAddress', 'City', 'State', 'Zip', 'location'].includes(field));
    }
    if (currentStep === 2) return hydratedForm.distanceValue ? [] : ['distanceValue'];
    if (currentStep === 3) return selectedClassTypes.length > 0 || customClassTypes.length > 0 ? [] : ['ClassTypes'];
    if (currentStep === 4) return stripeSetupComplete || stripeSkipped ? [] : ['stripe'];
    if (currentStep === 6) return agreementAccepted ? [] : ['agreementAccepted'];
    return [];
  };

  const stepMissingFields = getStepMissingFields();
  const canContinue = stepMissingFields.length === 0;
  const hasInvalidPhone = Boolean(String(form.Phone || '').trim())
    && !isValidOnboardingPhoneNumber(form.Phone);

  const handleNext = () => {
    if (!canContinue) return;
    setCurrentStep((step) => Math.min(step + 1, STEPS.length - 1));
  };

  const handleBack = () => {
    setCurrentStep((step) => Math.max(step - 1, 0));
  };

  const saveOnboarding = async ({ skippedStripe = stripeSkipped } = {}) => {
    if (!userDocId) return;

    setIsSaving(true);
    try {
      const nowMs = Date.now();
      const payload = buildInstructorOnboardingUpdate({
        formData: hydratedForm,
        stripeSkipped: skippedStripe,
        nowMs,
      });

      await updateDoc(doc(db, 'Users', userDocId), payload);
      await addDoc(collection(db, 'Users', userDocId, 'Activity'), buildInstructorOnboardingCompletedActivity({
        email: userData?.Email || user?.email,
        userDocId,
        stripeSkipped: skippedStripe,
        nowMs,
      }));

      if (skippedStripe) {
        await addDoc(collection(db, 'Users', userDocId, 'Activity'), buildInstructorOnboardingStripeSkippedActivity({
          email: userData?.Email || user?.email,
          userDocId,
          nowMs,
        }));
      }

      await refreshUserData?.();
      setShowSuccess(true);
      successTimerRef.current = window.setTimeout(() => {
        setShowSuccess(false);
      }, 2200);
    } catch (error) {
      toast({
        title: 'Unable to complete onboarding',
        description: error.message,
        status: 'error',
        duration: 4000,
        isClosable: true,
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleOpenStripe = async () => {
    try {
      await openStripeSetup();
    } catch (error) {
      toast({
        title: 'Unable to open Stripe setup',
        description: error.message,
        status: 'error',
        duration: 4000,
        isClosable: true,
      });
    }
  };

  const confirmSkipStripe = async () => {
    setStripeSkipped(true);
    skipStripeDisclosure.onClose();
    setCurrentStep(5);
  };

  const resetDocumentUpload = () => {
    setDocumentCategory('Insurance');
    setDocumentFile(null);
    setDocumentNotes('');
    setDocumentUploadProgress(0);
    if (documentInputRef.current) documentInputRef.current.value = '';
  };

  const handleAgreementScroll = (event) => {
    const { scrollHeight, scrollTop, clientHeight } = event.currentTarget;
    if (scrollTop + clientHeight >= scrollHeight - 12) {
      setHasScrolledAgreement(true);
    }
  };

  const handleOnboardingDocumentUpload = async () => {
    if (!userDocId || !documentFile) {
      toast({
        title: 'Choose a file first',
        status: 'warning',
        duration: 2500,
        isClosable: true,
      });
      return;
    }

    setIsUploadingDocument(true);

    try {
      const timestamp = Date.now();
      const storagePath = `instructor-uploads/${userDocId}/${timestamp}_${documentFile.name}`;
      const uploadTask = uploadBytesResumable(ref(storage, storagePath), documentFile);

      await waitForInstructorDocumentUpload(uploadTask, setDocumentUploadProgress);

      const url = await getDownloadURL(uploadTask.snapshot.ref);
      const entry = buildInstructorDocumentEntry({
        url,
        category: documentCategory,
        fileName: documentFile.name,
        notes: documentNotes,
        uploadedBy: user?.email || userData?.Email,
        date: timestamp,
      });

      await updateDoc(doc(db, 'Users', userDocId), {
        InstructorDocuments: arrayUnion(entry),
      });

      await addDoc(collection(db, 'Users', userDocId, 'Activity'), {
        type: 'document_upload',
        title: `Document Uploaded: ${documentFile.name}`,
        body: `Type: ${documentCategory}${documentNotes ? `\nNotes: ${documentNotes}` : ''}`,
        timestamp,
        actor: {
          userDocId,
          email: user?.email || userData?.Email || '',
        },
        metadata: {
          source: 'instructor_onboarding',
          category: documentCategory,
        },
      });

      await refreshUserData?.();
      toast({
        title: 'Document uploaded',
        status: 'success',
        duration: 2500,
        isClosable: true,
      });
      resetDocumentUpload();
    } catch (error) {
      toast({
        title: 'Upload failed',
        description: error.message,
        status: 'error',
        duration: 4000,
        isClosable: true,
      });
    } finally {
      setIsUploadingDocument(false);
    }
  };

  const renderIdentityStep = () => (
    <VStack align="stretch" spacing="18px">
      <Box>
        <Text color={textColor} fontSize="2xl" fontWeight="900">Welcome to Fit Group USA</Text>
        <Text color={mutedText} mt="8px">
          Let us tune your instructor profile so nearby classes and payout setup feel effortless.
        </Text>
      </Box>
      <SimpleGrid columns={{ base: 1, md: 2 }} gap="16px">
        <FormControl isRequired>
          <FormLabel>First Name</FormLabel>
          <Input value={form.FirstName} onChange={(event) => updateField('FirstName', event.target.value)} bg={inputBg} />
        </FormControl>
        <FormControl isRequired>
          <FormLabel>Last Name</FormLabel>
          <Input value={form.LastName} onChange={(event) => updateField('LastName', event.target.value)} bg={inputBg} />
        </FormControl>
      </SimpleGrid>
      <FormControl isRequired isInvalid={hasInvalidPhone}>
        <FormLabel>Phone Number</FormLabel>
        <Input type="tel" value={form.Phone} onChange={(event) => updateField('Phone', event.target.value)} bg={inputBg} />
        <FormErrorMessage>Enter a valid phone number.</FormErrorMessage>
      </FormControl>
    </VStack>
  );

  const renderAddressStep = () => (
    <VStack align="stretch" spacing="18px">
      <Box>
        <Text color={textColor} fontSize="2xl" fontWeight="900">Where should we find opportunities for you?</Text>
        <Text color={mutedText} mt="8px">
          Choose your home base with Google suggestions, then adjust the details if needed.
        </Text>
      </Box>
      <FormControl isRequired>
        <FormLabel>Address</FormLabel>
        <GoogleMapsLoader
          loadingElement={(
            <Input value={form.Address} onChange={(event) => updateField('Address', event.target.value)} bg={inputBg} />
          )}
        >
          <Autocomplete
            options={US_ADDRESS_AUTOCOMPLETE_OPTIONS}
            onLoad={(autocomplete) => {
              autocompleteRef.current = autocomplete;
            }}
            onPlaceChanged={handlePlaceChanged}
          >
            <Input
              value={form.Address}
              onChange={(event) => updateField('Address', event.target.value)}
              placeholder="Start typing your address"
              bg={inputBg}
              autoComplete="off"
            />
          </Autocomplete>
        </GoogleMapsLoader>
      </FormControl>
      <SimpleGrid columns={{ base: 1, md: 3 }} gap="16px">
        <FormControl isRequired>
          <FormLabel>City</FormLabel>
          <Input value={form.City} onChange={(event) => updateField('City', event.target.value)} bg={inputBg} />
        </FormControl>
        <FormControl isRequired>
          <FormLabel>State</FormLabel>
          <Input value={form.State} onChange={(event) => updateField('State', event.target.value)} bg={inputBg} />
        </FormControl>
        <FormControl isRequired>
          <FormLabel>Zip</FormLabel>
          <Input value={form.Zip} onChange={(event) => updateField('Zip', event.target.value)} bg={inputBg} />
        </FormControl>
      </SimpleGrid>
      <Alert status={hydratedForm.location ? 'success' : 'warning'} borderRadius="14px">
        <AlertIcon />
        {hydratedForm.location
          ? 'Address location confirmed.'
          : 'Select a Google suggestion so we can match classes around your real location.'}
      </Alert>
    </VStack>
  );

  const renderTravelStep = () => (
    <VStack align="stretch" spacing="18px">
      <Box>
        <Text color={textColor} fontSize="2xl" fontWeight="900">How far would you like to travel?</Text>
        <Text color={mutedText} mt="8px">Pick the range that keeps your schedule exciting without overdoing the drive.</Text>
      </Box>
      <SimpleGrid columns={{ base: 1, md: 3 }} gap="16px">
        {TRAVEL_DISTANCE_OPTIONS.map((option) => {
          const isSelected = form.distanceValue === option.value;

          return (
            <Button
              key={option.value}
              h="150px"
              whiteSpace="normal"
              alignItems="stretch"
              justifyContent="stretch"
              variant="outline"
              borderWidth="2px"
              borderColor={isSelected ? brandColor : borderColor}
              bg={isSelected ? selectedBg : 'transparent'}
              onClick={() => updateField('distanceValue', option.value)}
            >
              <Flex direction="column" align="flex-start" textAlign="left" gap="8px" w="100%">
                <Badge colorScheme={isSelected ? 'blue' : 'gray'} w="fit-content">{option.value}</Badge>
                <Text color={textColor} fontSize="lg" fontWeight="900">{option.title}</Text>
                <Text color={mutedText} fontSize="sm">{option.description}</Text>
              </Flex>
            </Button>
          );
        })}
      </SimpleGrid>
    </VStack>
  );

  const renderClassTypesStep = () => (
    <VStack align="stretch" spacing="18px">
      <Box>
        <Text color={textColor} fontSize="2xl" fontWeight="900">What do you love teaching?</Text>
        <Text color={mutedText} mt="8px">Choose every class type you are comfortable leading.</Text>
      </Box>
      <Card p="16px" bg={panelBg} border="1px solid" borderColor={borderColor}>
        <FormControl>
          <FormLabel>
            Other class type
            <Badge ml="8px" colorScheme="purple" borderRadius="full">User entered</Badge>
          </FormLabel>
          <Flex gap="10px" direction={{ base: 'column', md: 'row' }}>
            <Input
              value={customClassTypeInput}
              onChange={(event) => setCustomClassTypeInput(event.target.value)}
              onKeyDown={handleCustomClassTypeKeyDown}
              placeholder="Type a class type and press Enter"
              bg={inputBg}
            />
            <Button colorScheme="brand" onClick={addCustomClassType} isDisabled={!customClassTypeInput.trim()}>
              Add
            </Button>
          </Flex>
          <Text color={mutedText} fontSize="sm" mt="8px">
            If your specialty is not listed below, add it here and it will appear as a chip with your selected types.
          </Text>
        </FormControl>
      </Card>
      {selectedClassTypes.length > 0 && (
        <HStack spacing="8px" flexWrap="wrap">
          {selectedClassTypes.map((classType) => (
            <Tag key={classType} colorScheme="blue" borderRadius="full" py="6px">
              <TagLabel>{classType}</TagLabel>
              <TagCloseButton onClick={() => toggleClassType(classType)} />
            </Tag>
          ))}
        </HStack>
      )}
      {customClassTypes.length > 0 && (
        <HStack spacing="8px" flexWrap="wrap">
          {customClassTypes.map((classType) => (
            <Tag key={classType} colorScheme="purple" borderRadius="full" py="6px">
              <TagLabel>{classType}</TagLabel>
              <Badge ml="8px" colorScheme="purple" borderRadius="full">User entered</Badge>
              <TagCloseButton onClick={() => removeCustomClassType(classType)} />
            </Tag>
          ))}
        </HStack>
      )}
      <SimpleGrid columns={{ base: 2, md: 3, xl: 4 }} gap="14px">
        {availableClassTypes.map((classType) => {
          const isSelected = selectedClassTypes.includes(classType);
          const icon = getClassTypeIcon(classType);

          return (
            <Button
              key={classType}
              h="130px"
              p="12px"
              whiteSpace="normal"
              variant="outline"
              borderWidth="2px"
              borderColor={isSelected ? brandColor : borderColor}
              bg={isSelected ? selectedBg : 'transparent'}
              onClick={() => toggleClassType(classType)}
            >
              <Flex direction="column" align="center" justify="center" gap="8px" h="100%">
                {icon ? <Image src={icon} alt={`${classType} icon`} boxSize="42px" objectFit="contain" /> : <Icon as={MdClass} />}
                <Text color={textColor} fontSize="sm" fontWeight="800" textAlign="center">{classType}</Text>
                {isSelected && <Icon as={MdCheck} color={brandColor} />}
              </Flex>
            </Button>
          );
        })}
      </SimpleGrid>
    </VStack>
  );

  const renderStripeStep = () => (
    <VStack align="stretch" spacing="18px">
      {stripeSetupComplete ? (
        <Alert status="success" borderRadius="18px" p="20px">
          <AlertIcon />
          Stripe setup looks complete. You are ready to claim opportunities.
        </Alert>
      ) : (
        <>
          <Box>
            <Text color={textColor} fontSize="2xl" fontWeight="900">Set up Stripe payouts</Text>
            <Text color={mutedText} mt="8px">
              We use Stripe as our payment processor so your class payouts can move securely.
            </Text>
          </Box>
          <Card p="22px" bg={softBg} border="1px solid" borderColor="blue.100">
            <Flex gap="16px" align={{ base: 'flex-start', md: 'center' }} direction={{ base: 'column', md: 'row' }}>
              <Flex boxSize="56px" borderRadius="18px" bg="brand.500" align="center" justify="center" flexShrink={0}>
                <Icon as={MdCreditCard} color="white" boxSize="28px" />
              </Flex>
              <Box flex="1">
                <Text color={textColor} fontSize="lg" fontWeight="900">Payout setup can happen now or later</Text>
                <Text color={mutedText} mt="6px">
                  You can explore assigned classes without Stripe, but you will not be able to claim new classes until Stripe is complete.
                </Text>
              </Box>
              <Button colorScheme="brand" onClick={handleOpenStripe} isLoading={stripeSetupLinkLoading}>
                Open Stripe Setup
              </Button>
            </Flex>
          </Card>
        </>
      )}
    </VStack>
  );

  const renderDocumentsStep = () => (
    <VStack align="stretch" spacing="18px">
      <Box>
        <Text color={textColor} fontSize="2xl" fontWeight="900">Add documents now or later</Text>
        <Text color={mutedText} mt="8px">
          Upload insurance, certifications, or other relevant documents so the Fit Group USA team has what they need.
        </Text>
      </Box>
      <SimpleGrid columns={{ base: 1, md: 3 }} gap="14px">
        {INSTRUCTOR_DOCUMENT_SECTIONS.map((section) => (
          <Card key={section} p="16px" bg={panelBg} border="1px solid" borderColor={borderColor}>
            <Flex align="center" gap="12px">
              <Flex boxSize="42px" borderRadius="14px" bg={softBg} align="center" justify="center">
                <Icon as={section === 'Insurance' ? MdCreditCard : section === 'Certifications' ? MdCheckCircle : MdFolder} color={brandColor} boxSize="22px" />
              </Flex>
              <Box>
                <Text color={textColor} fontWeight="900">{section}</Text>
                <Text color={mutedText} fontSize="sm">Optional upload</Text>
              </Box>
            </Flex>
          </Card>
        ))}
      </SimpleGrid>
      <Card p="18px" bg={softBg} border="1px dashed" borderColor="brand.300">
        <Flex direction="column" align="center" justify="center" gap="12px" minH="150px" textAlign="center">
          <Icon as={MdCloudUpload} color={brandColor} boxSize="42px" />
          <Box>
            <Text color={textColor} fontWeight="900">Choose an instructor document</Text>
            <Text color={mutedText} fontSize="sm">PDFs and images work well here.</Text>
          </Box>
          <Input
            ref={documentInputRef}
            data-testid="instructor-onboarding-document-input"
            type="file"
            display="none"
            onChange={(event) => setDocumentFile(event.target.files?.[0] || null)}
          />
          <Button colorScheme="brand" variant="outline" onClick={() => documentInputRef.current?.click()}>
            Choose File
          </Button>
          <Text color={mutedText} fontSize="sm">
            {documentFile ? documentFile.name : 'No file selected'}
          </Text>
        </Flex>
      </Card>
      <SimpleGrid columns={{ base: 1, md: 2 }} gap="16px">
        <FormControl>
          <FormLabel>Type</FormLabel>
          <Box position="relative">
            <Button
              aria-label={`Type: ${documentCategory}`}
              w="100%"
              h="44px"
              bg={inputBg}
              border="1px solid"
              borderColor={borderColor}
              borderRadius="12px"
              fontWeight="700"
              rightIcon={<Icon as={MdKeyboardArrowDown} />}
              justifyContent="space-between"
              onClick={() => setIsDocumentTypeOpen((isOpen) => !isOpen)}
              _hover={{ bg: panelBg }}
              _active={{ bg: panelBg }}
            >
              <Flex align="center" justify="space-between" w="100%">
                <Text>{documentCategory}</Text>
              </Flex>
            </Button>
            {isDocumentTypeOpen && (
              <VStack
                role="menu"
                align="stretch"
                spacing="4px"
                position="absolute"
                top="50px"
                left={0}
                right={0}
                zIndex={20}
                bg={inputBg}
                border="1px solid"
                borderColor={borderColor}
                borderRadius="14px"
                boxShadow="lg"
                p="6px"
              >
                {INSTRUCTOR_DOCUMENT_SECTIONS.map((section) => (
                  <Button
                    key={section}
                    role="menuitem"
                    variant="ghost"
                    justifyContent="flex-start"
                    borderRadius="10px"
                    fontWeight={section === documentCategory ? '800' : '600'}
                    leftIcon={<Icon as={section === 'Insurance' ? MdCreditCard : section === 'Certifications' ? MdCheckCircle : MdFolder} color={section === documentCategory ? brandColor : mutedText} />}
                    onClick={() => {
                      setDocumentCategory(section);
                      setIsDocumentTypeOpen(false);
                    }}
                  >
                    {section}
                  </Button>
                ))}
              </VStack>
            )}
          </Box>
        </FormControl>
        <FormControl>
          <FormLabel>Notes</FormLabel>
          <Textarea
            value={documentNotes}
            onChange={(event) => setDocumentNotes(event.target.value)}
            placeholder="Optional notes"
            bg={inputBg}
          />
        </FormControl>
      </SimpleGrid>
      {isUploadingDocument && <Progress value={documentUploadProgress} colorScheme="blue" borderRadius="8px" />}
      <Flex justify="flex-end">
        <Button
          leftIcon={<Icon as={MdCloudUpload} />}
          colorScheme="brand"
          onClick={handleOnboardingDocumentUpload}
          isLoading={isUploadingDocument}
          isDisabled={!documentFile}
        >
          Upload Document
        </Button>
      </Flex>
    </VStack>
  );

  const renderAgreementStep = () => (
    <VStack align="stretch" spacing="18px">
      <Box>
        <Text color={textColor} fontSize="2xl" fontWeight="900">Instructor Agreement</Text>
        <Text color={mutedText} mt="8px">
          Please read the full agreement. The confirmation checkbox unlocks after you scroll to the bottom.
        </Text>
      </Box>
      <Alert status={hasScrolledAgreement ? 'success' : 'info'} borderRadius="14px">
        <AlertIcon />
        {hasScrolledAgreement
          ? 'You have reached the bottom of the agreement.'
          : 'Scroll to the bottom of the agreement to enable the checkbox.'}
      </Alert>
      <Box
        data-testid="instructor-onboarding-agreement-scroll"
        border="1px solid"
        borderColor={borderColor}
        borderRadius="18px"
        overflowY="auto"
        maxH={{ base: '320px', md: '420px' }}
        bg={inputBg}
        onScroll={handleAgreementScroll}
      >
        <Box
          as="iframe"
          title="Business Service Provider Agreement Instructor Agreement"
          src={INSTRUCTOR_AGREEMENT_VIEWER_URL}
          w="100%"
          h="900px"
          border="0"
        />
      </Box>
      <Tooltip
        label="Scroll to the bottom of the agreement to enable the checkbox."
        isDisabled={hasScrolledAgreement}
        hasArrow
      >
        <Box w="fit-content">
          <Checkbox
            colorScheme="blue"
            isDisabled={!hasScrolledAgreement}
            isChecked={agreementAccepted}
            onChange={(event) => setAgreementAccepted(event.target.checked)}
          >
            {AGREEMENT_CHECKBOX_LABEL}
          </Checkbox>
        </Box>
      </Tooltip>
    </VStack>
  );

  const renderStepContent = () => {
    if (currentStep === 0) return renderIdentityStep();
    if (currentStep === 1) return renderAddressStep();
    if (currentStep === 2) return renderTravelStep();
    if (currentStep === 3) return renderClassTypesStep();
    if (currentStep === 4) return renderStripeStep();
    if (currentStep === 5) return renderDocumentsStep();
    return renderAgreementStep();
  };

  if (showSuccess) {
    return (
      <Modal isOpen isCentered size="xl">
        <ModalOverlay bg="blackAlpha.600" backdropFilter="blur(6px)" />
        <ModalContent borderRadius="28px" bg={modalBg} p="34px" data-testid="instructor-onboarding-success">
          <Flex direction="column" align="center" textAlign="center" py="28px">
            <Flex
              boxSize="118px"
              borderRadius="full"
              bg="brand.500"
              align="center"
              justify="center"
              sx={{
                '@keyframes onboardingPulse': {
                  '0%, 100%': { boxShadow: '0 0 0 0 rgba(43, 132, 196, 0.36)' },
                  '50%': { boxShadow: '0 0 0 22px rgba(43, 132, 196, 0)' },
                },
                animation: 'onboardingPulse 1.8s ease-in-out infinite',
              }}
            >
              <Icon as={MdCheckCircle} boxSize="58px" color="white" />
            </Flex>
            <Text color={textColor} fontSize="3xl" fontWeight="900" mt="26px">You are ready to roll</Text>
            <Text color={mutedText} mt="10px" maxW="420px">
              Your instructor profile is set up. We will use this to match nearby classes and keep your portal feeling personal.
            </Text>
          </Flex>
        </ModalContent>
      </Modal>
    );
  }

  return (
    <>
          <Modal isOpen={shouldShow} isCentered size="5xl" closeOnEsc={false} closeOnOverlayClick={false}>
            <ModalOverlay bg="blackAlpha.600" backdropFilter="blur(6px)" />
            <ModalContent
              data-testid="instructor-onboarding-modal"
              bg={modalBg}
              borderRadius={{ base: 0, md: '28px' }}
              overflow="hidden"
              display="flex"
              flexDirection="column"
              minH={{ base: '100dvh', md: 'auto' }}
              maxH={{ base: '100dvh', md: 'calc(100vh - 48px)' }}
              my={{ base: 0, md: '3.75rem' }}
              boxShadow="0 24px 80px rgba(15, 23, 42, 0.28)"
            >
              <ModalHeader bg="brand.500" color="white" px={{ base: '16px', md: '34px' }} py={{ base: '16px', md: '24px' }} flexShrink={0}>
                <Flex align="center" justify="space-between" gap={{ base: '10px', md: '18px' }} direction="row">
                  <Box>
                    <Text fontSize="sm" fontWeight="800" opacity={0.86}>Instructor Portal Setup</Text>
                    <Text fontSize={{ base: 'xl', md: '3xl' }} fontWeight="900" mt="4px" noOfLines={1}>{STEPS[currentStep].label}</Text>
                  </Box>
                  <Badge bg="whiteAlpha.300" color="white" borderRadius="full" px={{ base: '10px', md: '12px' }} py="6px" flexShrink={0}>
                    Step {currentStep + 1} of {STEPS.length}
                  </Badge>
                </Flex>
                <Progress value={progressPercent} mt={{ base: '14px', md: '20px' }} borderRadius="full" colorScheme="blue" bg="whiteAlpha.300" />
              </ModalHeader>
              <ModalBody
                p={{ base: '16px', md: '34px' }}
                display="flex"
                flexDirection="column"
                flex="1"
            minH={0}
            overflow="hidden"
          >
              <HStack
                data-testid="instructor-onboarding-stepper"
                spacing={{ base: '8px', md: '10px' }}
                mb={{ base: '16px', md: '26px' }}
                overflowX={{ base: 'auto', md: 'visible' }}
                overflowY="visible"
                flexWrap={{ base: 'nowrap', md: 'wrap' }}
                pb="8px"
                align="center"
                flexShrink={0}
                sx={{ scrollbarWidth: 'none', '&::-webkit-scrollbar': { display: 'none' } }}
              >
              {STEPS.map((step, index) => {
                const isActive = index === currentStep;
                const isDone = index < currentStep;
                return (
                  <Flex
                    key={step.label}
                    aria-label={`${step.label} ${isDone ? 'complete' : isActive ? 'current' : 'upcoming'}`}
                    data-step-state={isDone ? 'complete' : isActive ? 'current' : 'upcoming'}
                    align="center"
                    justify={isActive ? 'flex-start' : 'center'}
                    gap={isActive ? '10px' : '0'}
                    h="44px"
                    minW="44px"
                    maxW={isActive ? { base: '170px', md: '230px' } : '44px'}
                    px={isActive ? '12px' : '0'}
                    borderRadius="16px"
                    bg={isActive ? softBg : isDone ? 'brand.500' : panelBg}
                    border="1px solid"
                    borderColor={isActive ? 'blue.100' : isDone ? 'brand.500' : borderColor}
                    flex={isActive ? '1 0 auto' : '0 0 44px'}
                    transition="all 0.22s ease"
                    position="relative"
                    overflow="visible"
                  >
                    <Icon as={step.icon} color={isActive ? brandColor : isDone ? 'white' : brandColor} boxSize="20px" flexShrink={0} />
                    {isDone && (
                      <Flex
                        data-testid={`instructor-onboarding-step-check-${index}`}
                        position="absolute"
                        top="-7px"
                        right="-7px"
                        boxSize="20px"
                        borderRadius="full"
                        bg="green.400"
                        border="2px solid"
                        borderColor={modalBg}
                        align="center"
                        justify="center"
                        boxShadow="0 6px 14px rgba(56, 161, 105, 0.32)"
                        zIndex={2}
                        sx={{
                          '@keyframes stepCheckPop': {
                            '0%': { opacity: 0, transform: 'scale(0.45) translateY(4px)' },
                            '70%': { opacity: 1, transform: 'scale(1.16) translateY(0)' },
                            '100%': { opacity: 1, transform: 'scale(1) translateY(0)' },
                          },
                          animation: 'stepCheckPop 0.22s ease-out',
                        }}
                      >
                        <Icon as={MdCheck} color="white" boxSize="13px" />
                      </Flex>
                    )}
                    {isActive && (
                      <ScaleFade initialScale={0.96} in>
                        <Text color={textColor} fontWeight="900" fontSize="sm" whiteSpace="nowrap">
                          {step.label}
                        </Text>
                      </ScaleFade>
                    )}
                  </Flex>
                );
              })}
            </HStack>
            <Box
              data-testid="instructor-onboarding-step-scroll"
              flex="1"
              minH={0}
              maxH={{ base: '100%', md: '100%' }}
              overflowY="auto"
              pr={{ base: '2px', md: '8px' }}
            >
              <ScaleFade key={currentStep} initialScale={0.98} in>
                {renderStepContent()}
              </ScaleFade>
            </Box>
            {!canContinue && currentStep !== 4 && (
              <Alert status="warning" borderRadius="14px" mt="20px" flexShrink={0}>
                <AlertIcon />
                Finish this step to continue.
              </Alert>
            )}
                <Flex
                  data-testid="instructor-onboarding-actions"
                  justify="space-between"
                  align={{ base: 'stretch', sm: 'center' }}
                  direction={{ base: 'column', sm: 'row' }}
                  mt="18px"
                  pt={{ base: '14px', md: '18px' }}
                  gap="14px"
                  flexShrink={0}
                  bg={modalBg}
              borderTop="1px solid"
              borderColor={borderColor}
            >
              <Button variant="ghost" onClick={handleBack} isDisabled={currentStep === 0 || isSaving}>Back</Button>
                  <HStack w={{ base: '100%', sm: 'auto' }} justify={{ base: 'flex-end', sm: 'flex-start' }} flexWrap="wrap">
                {currentStep === 4 && !stripeSetupComplete && !stripeSkipped && (
                  <Button variant="outline" colorScheme="orange" onClick={skipStripeDisclosure.onOpen} isDisabled={isSaving}>
                    Skip Stripe
                  </Button>
                )}
                {currentStep === 5 && (
                  <Button
                    variant="outline"
                    colorScheme="blue"
                    onClick={() => setCurrentStep(6)}
                    isLoading={isSaving}
                    isDisabled={isUploadingDocument}
                  >
                    Skip Documents
                  </Button>
                )}
                {currentStep === STEPS.length - 1 ? (
                  <Button
                    colorScheme="brand"
                    onClick={() => saveOnboarding({ skippedStripe: stripeSkipped })}
                    isLoading={isSaving}
                    isDisabled={isUploadingDocument || !canContinue}
                  >
                    Finish Setup
                  </Button>
                ) : (
                  <Button colorScheme="brand" onClick={handleNext} isDisabled={!canContinue}>
                    Next
                  </Button>
                )}
              </HStack>
            </Flex>
          </ModalBody>
        </ModalContent>
      </Modal>

      <AlertDialog
        isOpen={skipStripeDisclosure.isOpen}
        leastDestructiveRef={cancelSkipRef}
        onClose={skipStripeDisclosure.onClose}
      >
        <AlertDialogOverlay>
          <AlertDialogContent borderRadius="20px">
            <AlertDialogHeader fontWeight="900">Skip Stripe setup?</AlertDialogHeader>
            <AlertDialogBody color={mutedText}>
              You will not be able to claim new classes until Stripe is complete.
            </AlertDialogBody>
            <AlertDialogFooter>
              <Button ref={cancelSkipRef} onClick={skipStripeDisclosure.onClose}>Go back</Button>
              <Button colorScheme="orange" ml={3} onClick={confirmSkipStripe} isLoading={isSaving}>
                Yes, skip for now
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </>
  );
}
